import './TravelTimeBubble.css';
import { DispatchCardDismiss } from './DispatchCardDismiss';

/**
 * V9.2.7 (2026-06-06) — TravelTimeBubble.
 *
 * Pure-presentation render of a `force_travel_time_compute` dispatch
 * action. Ch 19 Travel Matrices (12 air cities + 12 train cities + 9
 * driving cities × 2 speeds). Shows origin → destination + travel
 * mode. Resolved hours + timezone delta surface via optional props.
 *
 * Schema 1-to-1 with the V9.1.b tool args at routes/dispatch.py:713-753:
 *   - origin (string, required): origin city as it appears in the matrix
 *   - destination (string, required): destination city
 *   - mode: enum {train, air, driving_60mph, driving_100mph}
 *   - rationale: GM voice-line ≤30 words (optional)
 *
 * Routes to seed/canonical/travel/travel_matrices.json downstream.
 * London → New York by air = 7.5 hours per md L6708 worked example.
 */

export type TravelMode = 'train' | 'air' | 'driving_60mph' | 'driving_100mph';

export interface TravelTimeBubbleProps {
  origin: string;
  destination: string;
  mode: TravelMode;
  rationale?: string;
  /** Resolved travel hours from the matrix. Omit/null → pending state. */
  resolvedHours?: number | null;
  /** Resolved timezone delta in hours (e.g. +5 for London→Vegas air). */
  resolvedTimezoneDeltaHours?: number | null;
  /** V9.2.x.3: when provided, renders a ✕ dismiss button (top-right). */
  onDismiss?: () => void;
  testidPrefix?: string;
}

const MODE_LABEL: Record<TravelMode, string> = {
  train: 'Train',
  air: 'Air',
  driving_60mph: 'Driving (60mph)',
  driving_100mph: 'Driving (100mph)',
};

const MODE_ICON: Record<TravelMode, string> = {
  train: '🚂',
  air: '✈',
  driving_60mph: '🚗',
  driving_100mph: '🏎',
};

function formatHours(hours: number): string {
  if (Number.isInteger(hours)) return `${hours} hr`;
  return `${hours.toFixed(1)} hr`;
}

function formatTzDelta(delta: number): string {
  if (delta === 0) return 'Same time zone';
  const sign = delta > 0 ? '+' : '−';
  return `${sign}${Math.abs(delta)} hr TZ`;
}

export function TravelTimeBubble({
  origin,
  destination,
  mode,
  rationale,
  resolvedHours,
  resolvedTimezoneDeltaHours,
  onDismiss,
  testidPrefix = 'br-travel',
}: TravelTimeBubbleProps) {
  const hasHours = typeof resolvedHours === 'number' && resolvedHours >= 0;
  const hasTz = typeof resolvedTimezoneDeltaHours === 'number';

  return (
    <aside
      className="br-travel"
      data-testid={testidPrefix}
      data-mode={mode}
      data-state={hasHours ? 'resolved' : 'pending'}
    >
      <DispatchCardDismiss onDismiss={onDismiss} testidPrefix={testidPrefix} />
      <header className="br-travel-header">
        <span className="br-travel-label">Travel</span>
        <span
          className="br-travel-mode-chip"
          data-testid={`${testidPrefix}-mode-chip`}
          data-mode-id={mode}
        >
          <span className="br-travel-mode-icon" aria-hidden="true">
            {MODE_ICON[mode]}
          </span>
          <span className="br-travel-mode-label">{MODE_LABEL[mode]}</span>
        </span>
      </header>

      <div
        className="br-travel-route"
        data-testid={`${testidPrefix}-route`}
      >
        <span
          className="br-travel-origin"
          data-testid={`${testidPrefix}-origin`}
        >
          {origin}
        </span>
        <span
          className="br-travel-arrow"
          data-testid={`${testidPrefix}-arrow`}
          aria-hidden="true"
        >
          →
        </span>
        <span
          className="br-travel-destination"
          data-testid={`${testidPrefix}-destination`}
        >
          {destination}
        </span>
      </div>

      {hasHours ? (
        <div
          className="br-travel-resolved"
          data-testid={`${testidPrefix}-resolved`}
        >
          <span
            className="br-travel-hours br-label"
            data-testid={`${testidPrefix}-hours`}
          >
            {formatHours(resolvedHours)}
          </span>
          {hasTz && (
            <span
              className="br-travel-tz-delta br-label"
              data-testid={`${testidPrefix}-tz-delta`}
              data-tz-delta={resolvedTimezoneDeltaHours}
            >
              {formatTzDelta(resolvedTimezoneDeltaHours)}
            </span>
          )}
        </div>
      ) : (
        <p
          className="br-travel-pending"
          data-testid={`${testidPrefix}-pending`}
        >
          Awaiting matrix lookup…
        </p>
      )}

      {rationale !== undefined && rationale.length > 0 && (
        <p
          className="br-travel-rationale"
          data-testid={`${testidPrefix}-rationale`}
        >
          {rationale}
        </p>
      )}
    </aside>
  );
}
